"use client";

import { unwrapCountedObjects } from "@/components/ProjectsInArtwork/utils/artworkListApiTransform";
import { normalizeVendorAssessmentListItem } from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";
import type {
  PrepareForPanelListItem,
  PrepareForPanelListRequest,
  PrepareForPanelListResponse,
} from "../types/prepareForPanelListApi.types";

export {
  formatVendorAssessmentStatusLabel as formatPrepareForPanelStatusLabel,
  resolveVendorPublished as resolvePrepareForPanelPublished,
  isVendorAssessmentStatusValue as isPrepareForPanelVendorAssessmentStatus,
} from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

export function normalizePrepareForPanelListItem(
  raw: unknown,
  fallbackId: string
): PrepareForPanelListItem {
  return normalizeVendorAssessmentListItem(raw, fallbackId) as PrepareForPanelListItem;
}

export function normalizePrepareForPanelListResponse(
  raw: unknown,
  req: PrepareForPanelListRequest,
  totalFromHeader?: unknown
): PrepareForPanelListResponse {
  if (Array.isArray(raw)) {
    const headerTotal = toNumber(totalFromHeader);
    return {
      items: raw.map((it, idx) => normalizePrepareForPanelListItem(it, `${req.pageNo}-${idx}`)),
      totalCount: headerTotal ?? raw.length,
      pageNo: req.pageNo,
      pageSize: req.pageSize,
    };
  }

  const rawAsUnknown = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const itemsContainer =
    rawAsUnknown.items ??
    rawAsUnknown.data ??
    rawAsUnknown.content ??
    rawAsUnknown.objects ??
    [];
  const unwrappedItems = unwrapCountedObjects(itemsContainer);

  const headerTotal = toNumber(totalFromHeader);
  const bodyTotal =
    toNumber(rawAsUnknown.totalCount) ??
    toNumber(rawAsUnknown.totalElements) ??
    toNumber(rawAsUnknown.totalRecords) ??
    toNumber(rawAsUnknown.count);

  const normalizedItems = unwrappedItems.map((it, idx) =>
    normalizePrepareForPanelListItem(it, `${req.pageNo}-${idx}`)
  );

  return {
    items: normalizedItems,
    totalCount: headerTotal ?? bodyTotal ?? normalizedItems.length,
    pageNo: toNumber(rawAsUnknown.pageNo) ?? toNumber(rawAsUnknown.pageNumber) ?? req.pageNo,
    pageSize: toNumber(rawAsUnknown.pageSize) ?? toNumber(rawAsUnknown.size) ?? req.pageSize,
  };
}
